import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import { api } from "$convex/convexApi";
import { Copy, Pencil, Plus, Globe, MapPin, Users } from "lucide-react";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useProductsWithDetails } from "@/hooks/useProducts";
import { useAppLocale } from "@/lib/i18n";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { exportToCSV } from "@/lib/export";
import { BADGE_TONES, pickAssortedBadgeClass } from "@/lib/badge-tones";
import { m } from "@/paraglide/messages.js";

import { ProductCreateDialog } from "./product-create-dialog";
import { ProductEditDialog } from "./product-edit-dialog";
import { OriginManagementDialog } from "./origin-management-dialog";
import { ProducerManagementDialog } from "./producer-management-dialog";
import { RegionManagementDialog } from "./region-management-dialog";
import { AromaManagementDialog, FlavorManagementDialog } from "./tasting-note-management-dialog";

function statusBadge(archived?: boolean) {
  if (archived) return BADGE_TONES.zinc;
  return BADGE_TONES.slate;
}

function statusLabel(archived?: boolean) {
  if (archived) return m.status_archived();
  return m.status_active();
}

function useColumns() {
  useAppLocale();
  return React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.product()} />,
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
        meta: { label: m.product(), placeholder: m.search_product_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "origin",
        accessorFn: (row) => row.origin?.name ?? "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.origin()} />,
        cell: ({ row }) => {
          const origin = row.original.origin?.name;
          return origin ? (
            <Badge variant="outline" className={pickAssortedBadgeClass(origin)}>
              {origin}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">{m.not_available()}</span>
          );
        },
        meta: { label: m.origin(), placeholder: m.search_origin_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "region",
        accessorFn: (row) => row.region?.name ?? "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.region()} />,
        cell: ({ row }) => {
          const region = row.original.region?.name;
          return region ? (
            <Badge variant="outline" className={pickAssortedBadgeClass(region)}>
              {region}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">{m.not_available()}</span>
          );
        },
        meta: { label: m.region(), placeholder: m.search_region_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "producer",
        accessorFn: (row) => row.producer?.name ?? "",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.producer()} />,
        cell: ({ row }) => {
          const producer = row.original.producer?.name;
          return producer ? (
            <Badge variant="outline" className={pickAssortedBadgeClass(producer)}>
              {producer}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">{m.not_available()}</span>
          );
        },
        meta: { label: m.producer(), placeholder: m.search_producer_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "varietal",
        accessorKey: "varietal",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.varietal()} />,
        cell: ({ row }) => {
          const varietal = row.original.varietal;
          return varietal ? (
            <Badge variant="outline" className={pickAssortedBadgeClass(varietal)}>
              {varietal}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">{m.not_available()}</span>
          );
        },
        meta: { label: m.varietal(), placeholder: m.search_varietal_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "millingProcess",
        accessorKey: "millingProcess",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.process()} />,
        cell: ({ row }) => {
          const process = row.original.millingProcess || row.original.processId;
          return process ? (
            <Badge variant="outline" className={pickAssortedBadgeClass(process)}>
              {process}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">{m.not_available()}</span>
          );
        },
        meta: { label: m.process(), placeholder: m.search_process_placeholder(), variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "score",
        accessorKey: "score",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.score()} />,
        cell: ({ row }) => {
          const score = row.original.score;
          return score != null ? <span className="font-medium">{score}</span> : <span className="text-muted-foreground">{m.not_available()}</span>;
        },
        meta: { label: m.score(), variant: "number", placeholder: m.min_score() },
        enableColumnFilter: true,
      },
      {
        id: "weight",
        accessorKey: "weight",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.weight()} />,
        cell: ({ row }) => {
          const w = row.original.weight;
          return w != null ? <span>{m.inventory_weight_lbs({ weight: w })}</span> : <span className="text-muted-foreground">{m.not_available()}</span>;
        },
        meta: { label: m.weight(), variant: "number", placeholder: m.min_weight() },
      },
      {
        id: "acidity",
        accessorKey: "acidity",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.acidity()} />,
        cell: ({ row }) => <span className="text-sm">{row.original.acidity || m.not_available()}</span>,
        meta: { label: m.acidity(), variant: "text" },
      },
      {
        id: "bodyFeel",
        accessorKey: "bodyFeel",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.body()} />,
        cell: ({ row }) => <span className="text-sm">{row.original.bodyFeel || m.not_available()}</span>,
        meta: { label: m.body(), variant: "text" },
      },
      {
        id: "masl",
        accessorKey: "masl",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.masl()} />,
        cell: ({ row }) => <span className="text-sm">{row.original.masl || m.not_available()}</span>,
        meta: { label: "MASL", variant: "text" },
      },
      {
        id: "harvestCycle",
        accessorKey: "harvestCycle",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.harvest()} />,
        cell: ({ row }) => <span className="text-sm">{row.original.harvestCycle || m.not_available()}</span>,
        meta: { label: m.harvest(), variant: "text" },
      },
      {
        id: "grainPro",
        accessorKey: "grainPro",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.grainpro()} />,
        cell: ({ row }) => row.original.grainPro ? <Badge variant="outline" className={BADGE_TONES.emerald}>{m.yes()}</Badge> : <span className="text-muted-foreground">{m.no()}</span>,
      },
      {
        id: "samplesStock",
        accessorKey: "samplesStock",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.samples()} />,
        cell: ({ row }) => <span>{row.original.samplesStock ?? 0}</span>,
        meta: { label: m.samples(), variant: "number", placeholder: m.min_samples() },
        enableColumnFilter: true,
      },
      {
        id: "status",
        accessorKey: "archived",
        header: ({ column }) => <DataTableColumnHeader column={column} label={m.status()} />,
        cell: ({ row }) => (
          <Badge variant="outline" className={statusBadge(row.original.archived)}>
            {statusLabel(row.original.archived)}
          </Badge>
        ),
        meta: {
          label: m.status(),
          variant: "select",
          options: [
            { label: m.status_active(), value: "false" },
            { label: m.status_archived(), value: "true" },
          ],
        },
        enableColumnFilter: true,
      },
    ],
    [],
  );
}

export default function InventoryProductsPage() {
  const { locale } = useAppLocale();
  const products = useProductsWithDetails();
  const bulkUpdateProducts = useMutation(api.products.bulkUpdateProducts);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [duplicateProduct, setDuplicateProduct] = React.useState<any>(null);
  const [editProduct, setEditProduct] = React.useState<any>(null);
  const [originsOpen, setOriginsOpen] = React.useState(false);
  const [regionsOpen, setRegionsOpen] = React.useState(false);
  const [producersOpen, setProducersOpen] = React.useState(false);
  const [flavorsOpen, setFlavorsOpen] = React.useState(false);
  const [aromasOpen, setAromasOpen] = React.useState(false);

  const columns = useColumns();

  const activeProducts = products?.filter((p: any) => !p.archived) ?? [];
  const archivedProducts = products?.filter((p: any) => p.archived) ?? [];

  const handleCreateOpenChange = React.useCallback((open: boolean) => {
    setCreateOpen(open);
    if (!open) {
      setDuplicateProduct(null);
    }
  }, []);

  const handleCreateProductClick = React.useCallback(() => {
    setDuplicateProduct(null);
    setCreateOpen(true);
  }, []);

  const handleDuplicateProductClick = React.useCallback((product: any, clearSelection: () => void) => {
    setDuplicateProduct(product);
    setCreateOpen(true);
    clearSelection();
  }, []);

  const handleBulkProductUpdate = React.useCallback(
    async (
      selectedRows: any[],
      fields: { archived?: boolean },
      successLabel: string,
      clearSelection: () => void,
    ) => {
      const updates = selectedRows
        .map((row) => row._id ?? row.id)
        .filter(Boolean)
        .map((id) => ({ id, fields }));

      if (updates.length === 0) {
        toast.error(m.inventory_no_products_selected());
        return;
      }

      try {
        const result = await bulkUpdateProducts({ updates: updates as any[] });
        const failed = result.filter((item: any) => !item.success).length;
        const successful = result.length - failed;

        if (failed > 0) {
          toast.error(m.inventory_products_partial_update({ successful, total: result.length }));
        } else {
          toast.success(m.inventory_products_bulk_success({ action: successLabel, count: successful }));
        }
        clearSelection();
      } catch (error) {
        toast.error(
          toUserFacingErrorMessage(error, "Failed to update products", {
            unauthorizedMessage:
              m.inventory_products_unauthorized(),
          }),
        );
      }
    },
    [bulkUpdateProducts],
  );

  const handleExportSelected = React.useCallback((selectedRows: any[]) => {
    if (selectedRows.length === 0) {
      toast.error(m.inventory_no_products_selected());
      return;
    }

    exportToCSV(
      selectedRows.map((product) => ({
        name: product.name ?? "",
        origin: product.origin?.name ?? "",
        region: product.region?.name ?? "",
        producer: product.producer?.name ?? "",
        varietal: product.varietal ?? "",
        process: product.millingProcess ?? product.processId ?? "",
        score: product.score ?? "",
        weight: product.weight ?? "",
        acidity: product.acidity ?? "",
        bodyFeel: product.bodyFeel ?? "",
        masl: product.masl ?? "",
        harvestCycle: product.harvestCycle ?? "",
        grainPro: product.grainPro ? m.yes() : m.no(),
        samplesStock: product.samplesStock ?? 0,
        status: statusLabel(product.archived),
      })),
      [
        { key: "name", label: m.product() },
        { key: "origin", label: m.origin() },
        { key: "region", label: m.region() },
        { key: "producer", label: m.producer() },
        { key: "varietal", label: m.varietal() },
        { key: "process", label: m.process() },
        { key: "score", label: m.score() },
        { key: "weight", label: m.weight() },
        { key: "acidity", label: m.acidity() },
        { key: "bodyFeel", label: m.body() },
        { key: "masl", label: "MASL" },
        { key: "harvestCycle", label: m.harvest() },
        { key: "grainPro", label: m.grainpro() },
        { key: "samplesStock", label: m.samples() },
        { key: "status", label: m.status() },
      ],
      `${m.inventory_products_export_prefix()}-${new Date().toISOString().split("T")[0]}.csv`,
    );
  }, []);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>{m.inventory_products_title()}</CardTitle>
              <CardDescription>
                {m.inventory_products_description()}
                {products && (
                  <span className="ml-2 font-medium">
                    {m.inventory_products_count({ active: activeProducts.length, archived: archivedProducts.length })}
                  </span>
                )}
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setOriginsOpen(true)}>
                <Globe className="mr-1.5 h-3.5 w-3.5" />
                {m.origins()}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRegionsOpen(true)}>
                <MapPin className="mr-1.5 h-3.5 w-3.5" />
                {m.regions()}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setProducersOpen(true)}>
                <Users className="mr-1.5 h-3.5 w-3.5" />
                {m.producers()}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setFlavorsOpen(true)}>
                {locale === "es" ? "Sabores" : "Flavors"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAromasOpen(true)}>
                {locale === "es" ? "Aromas" : "Aromas"}
              </Button>
              <Button size="sm" onClick={handleCreateProductClick}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {m.create_product()}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {products === undefined ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
          <InternalEntityDataTable
            data={products}
            columns={columns}
            initialSorting={[{ id: "name", desc: false }]}
            actionBar={({ selectedRows, clearSelection }) => (
              <DataTableFloatingBar
                selectedCount={selectedRows.length}
                onClear={clearSelection}
              >
                {selectedRows.length === 1 && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditProduct(selectedRows[0]);
                        clearSelection();
                      }}
                    >
                      <Pencil className="mr-1.5 h-3.5 w-3.5" />
                      {m.edit()}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDuplicateProductClick(selectedRows[0], clearSelection)}
                    >
                      <Copy className="mr-1.5 h-3.5 w-3.5" />
                      {locale === "es" ? "Duplicar" : "Duplicate"}
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleExportSelected(selectedRows)}
                >
                  {m.export()}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() =>
                    void handleBulkProductUpdate(
                      selectedRows,
                      { archived: true },
                      m.status_archived(),
                      clearSelection,
                    )
                  }
                >
                  {m.archive()}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    void handleBulkProductUpdate(
                      selectedRows,
                      { archived: false },
                      m.restored(),
                      clearSelection,
                    )
                  }
                >
                  {m.restore()}
                </Button>
              </DataTableFloatingBar>
            )}
          />
          )}
        </CardContent>
      </Card>

      <ProductCreateDialog open={createOpen} onOpenChange={handleCreateOpenChange} duplicateFrom={duplicateProduct} />
      <ProductEditDialog open={!!editProduct} onOpenChange={(open) => { if (!open) setEditProduct(null); }} product={editProduct} />
      <OriginManagementDialog open={originsOpen} onOpenChange={setOriginsOpen} />
      <RegionManagementDialog open={regionsOpen} onOpenChange={setRegionsOpen} />
      <ProducerManagementDialog open={producersOpen} onOpenChange={setProducersOpen} />
      <FlavorManagementDialog open={flavorsOpen} onOpenChange={setFlavorsOpen} />
      <AromaManagementDialog open={aromasOpen} onOpenChange={setAromasOpen} />
    </>
  );
}
